import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TrackError } from '@fantaz/core/utils';
import { FantazFailure } from '@fantaz/shared/data-access/models';
import { ModalAlertComponent } from '@fantaz/shared/ui/modal-alert';
import { ToastService } from '@fantaz/shared/utils/toast';
import { Observable, delay, mergeMap, of, retryWhen, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { AuthServiceInjectionToken } from '@fantaz/shared/data-access/api';

// =============================================================================
// == Observer to implement retry logic
// =============================================================================
const getErrorMessage = (maxRetry: number) =>
  `Tried to load resource over XHR for ${maxRetry} times without success. Giving up.`;

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BACKOFF = 1000;
const DEFAULT_CODE_WHEN_RETRY = [502];

const retryWhenError = (options: {
  delayMs: number;
  maxRetry?: number;
  backoffMs?: number;
  codes?: number[];
}) => {
  const opts = {
    maxRetry: DEFAULT_MAX_RETRIES,
    backoffMs: DEFAULT_BACKOFF,
    codes: DEFAULT_CODE_WHEN_RETRY,
    ...options,
  };
  let retries = opts.maxRetry;

  return (src: Observable<any>) =>
    src.pipe(
      retryWhen((errors: Observable<any>) =>
        errors.pipe(
          mergeMap((error) => {
            if (retries-- > 0 && opts.codes.includes(error.status)) {
              const backoffTime =
                opts.delayMs + opts.backoffMs * (opts.maxRetry - retries);
              return of(error).pipe(delay(backoffTime));
            }

            return throwError(() => error);
          }),
        ),
      ),
    );
};

// =============================================================================
// == Http interceptor
// =============================================================================

@Injectable()
export class HttpLoadingInterceptor implements HttpInterceptor {
  #authService = inject(AuthServiceInjectionToken);
  dialogRef: MatDialogRef<ModalAlertComponent>;
  constructor(
    private toastService: ToastService,
    private dialog: MatDialog,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      retryWhenError({
        delayMs: 500,
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    ) as Observable<HttpEvent<any>>;
  }

  private handleError(error: HttpErrorResponse) {
    const { status } = error;
    const log =
      'You seem to have a bad network connection. Not to worry! You can try to re-establish the connection.';
    let fantazError;

    switch (status) {
      //
      // Unauthorized
      //
      case 401:
        fantazError = new FantazFailure('FantazFailure', 'HTTP-1401', error, {
          metadata: {
            status: 0,
          },
        });
        if (this.#authService.getUserId() !== '') {
          this.#authService
            .refreshSession()
            .pipe(take(1))
            .subscribe((session) => {
              console.log(session);
              if (!session.tokens) {
                this.showSessionExpirationModal();
              }
            });
        } else {
          this.showSessionExpirationModal();
        }
        break;
      case 404:
        fantazError = new FantazFailure('FantazFailure', 'HTTP-1404', error, {
          metadata: {
            status: 0,
          },
        });
        this.openDialog(log);
        break;
      case 0:
        fantazError = new FantazFailure('FantazFailure', 'HTTP-1000', error, {
          metadata: {
            status: 0,
          },
        });
        break;

      //
      //  Gateway Timeout
      //
      case 504:
        fantazError = new FantazFailure('FantazFailure', 'HTTP-1504', error, {
          metadata: {
            status: 0,
          },
        });
        break;

      default: {
        break;
      }
    }

    if (fantazError) {
      TrackError.trackError(fantazError);
    }

    // TODO: Return new Observable with error to be handled by the caller
    return throwError(() => {
      return error;
    });
  }

  private showSessionExpirationModal() {
    if (!this.dialogRef && !window.location.pathname.includes('auth/')) {
      this.dialogRef = this.dialog.open(ModalAlertComponent, {
        data: {
          modalType: 'SESSION_EXPIRED',
        },
      });
    }
  }

  openDialog(message: string) {
    this.toastService
      .openSnackBar(message || 'Undefined client error', {
        duration: 10000,
        action: 'Retry',
      })
      .onAction()
      .pipe(take(1))
      .subscribe(() => {
        location.reload();
      });
  }
}
